/**
 * Pieces of client approvals (R191; docs/specs/portal/client-approvals.md) used where each item lives: the state
 * chip (always words with its tone), "Send to client for approval" with an optional note, and the block change's
 * before and after on the project map's own component.
 */
import { useMemo, useState, type ReactElement } from 'react';
import {
  approvalDecisionLine,
  approvalNoteProblem,
  approvalStateText,
  approvalTone,
  approvalVersionText,
  blockChangeFeatures,
  blockChangeSummary,
  shortHash,
  type ApprovalRow,
  type BlockSnapshot,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TextArea } from '../components/Fields.tsx';
import { RemainingMap } from '../portal/RemainingMap.tsx';

/** The state in words with its tone (QC colours are never alone). */
export function ApprovalChip({
  row,
  reader = 'staff',
}: {
  readonly row: Pick<ApprovalRow, 'state' | 'kind'> &
    Partial<Pick<ApprovalRow, 'manager_name' | 'client_signed_at' | 'manager_signed_at'>>;
  readonly reader?: 'staff' | 'client';
}): ReactElement {
  const tone = approvalTone(row.state);
  return (
    <span className={tone === 'neutral' ? 'chip' : `chip chip--${tone}`}>
      {approvalStateText(row, reader)}
    </span>
  );
}

/** One line under an item: "Version 2 sent for approval: Waiting for the client. Sent by …". */
export function ApprovalLine({
  row,
  changed = false,
}: {
  readonly row: ApprovalRow;
  /** A block whose plan changed in Teslyn Cloud since this was sent. */
  readonly changed?: boolean;
}): ReactElement {
  return (
    <p className="caption">
      Client approval, {approvalVersionText(row).toLowerCase()} (
      <span className="data">{shortHash(row.version_hash)}</span>
      ): <ApprovalChip row={row} /> {approvalDecisionLine(row)}
      {changed && row.state === 'waiting' && (
        <> The block has changed since it was sent: send it again so the client sees the plan as it is now.</>
      )}
    </p>
  );
}

/** "Send to client for approval" with a note for the client; disabled with the reason when it cannot be sent. */
export function SendForApproval({
  label = 'Send to client for approval',
  what,
  disabledReason,
  busy,
  onSend,
}: {
  readonly label?: string;
  /** The item in words, for the button's accessible name ("block Milgun", "version 2"). */
  readonly what: string;
  readonly disabledReason?: string | null;
  readonly busy: boolean;
  readonly onSend: (note: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const problem = approvalNoteProblem(note);
  if (disabledReason)
    return (
      <span className="caption muted" title={disabledReason}>
        {disabledReason}
      </span>
    );
  if (!open)
    return (
      <Button variant="secondary" onClick={() => setOpen(true)} aria-label={`${label}: ${what}`}>
        {label}
      </Button>
    );
  return (
    <form
      className="stack grow"
      aria-label={`${label}: ${what}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!problem) {
          onSend(note.trim());
          setOpen(false);
          setNote('');
        }
      }}
    >
      <TextArea
        label="Note for the client (optional)"
        rows={2}
        value={note}
        onChange={setNote}
        error={problem ?? undefined}
        hint="The client sees it with the request. A newer version sent later replaces one still waiting."
      />
      <div className="row">
        <Button type="submit" variant="primary" busy={busy} disabled={!!problem}>
          {busy ? 'Sending…' : 'Send'}
        </Button>
        <Button variant="quiet" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The block as sent beside the block as last approved, drawn with the project map, and the change in words. */
export function BlockChangeView({
  after,
  before,
}: {
  readonly after: BlockSnapshot;
  readonly before: BlockSnapshot | null;
}): ReactElement {
  const summary = useMemo(() => blockChangeSummary(before, after), [before, after]);
  const now = useMemo(() => blockChangeFeatures(after, 'after'), [after]);
  const was = useMemo(() => (before ? blockChangeFeatures(before, 'before') : null), [before]);
  const notDrawn = [...now.notDrawn, ...(was?.notDrawn ?? [])];
  return (
    <div className="stack" data-basemap="imagery">
      <ul className="stack" aria-label="What changed">
        {summary.words.map((w) => (
          <li key={w} className="body">
            {w}
          </li>
        ))}
      </ul>
      <div className="approval-maps">
        {was && (
          <figure className="stack approval-map">
            <figcaption className="label">Before: as you last approved it (boundary dashed)</figcaption>
            <RemainingMap
              features={was.features}
              label={`Block ${before!.block.name} as last approved. ${summary.words.join(' ')}`}
              fitKey={`before:${before!.block.id}`}
              className="approval-map__map"
            />
          </figure>
        )}
        <figure className="stack approval-map">
          <figcaption className="label">{was ? 'After: as sent now' : 'The block as sent'}</figcaption>
          <RemainingMap
            features={now.features}
            label={`Block ${after.block.name} as sent. ${summary.words.join(' ')}`}
            fitKey={`after:${after.block.id}`}
            className="approval-map__map"
          />
        </figure>
      </div>
      {notDrawn.length > 0 && <p className="caption muted">Not drawn: {notDrawn.join('; ')}.</p>}
    </div>
  );
}
